import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { LIST_TRAININGS, CREATE_TRAINING, UPDATE_TRAINING } from '../graphql/trainingOperations';
import { toast } from 'react-toastify';
import { GET_ALL_CATEGORIES } from '../graphql/categoryOperations';
import { GET_ALL_LEVELS } from '../graphql/exerciseOperations';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import {
  PlusIcon,
  FunnelIcon,
  ArrowPathIcon,
  PencilIcon
} from '@heroicons/react/24/solid';
import { useTheme } from '../contexts/ThemeContext';
import AddExercisesToTrainingModal from '../components/AddExercisesToTrainingModal';

const TrainingsManagement = () => {
  const { isDarkMode } = useTheme();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);
  const [isAddExercisesModalOpen, setIsAddExercisesModalOpen] = useState(false);
  const [trainingData, setTrainingData] = useState({
    name: '',
    categoryId: '',
    levelId: ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [editThumbnailFile, setEditThumbnailFile] = useState(null);
  const [filters, setFilters] = useState({
    name: null,
    categoryId: null,
    levelId: null
  });

  // Create and update training mutations
  const [createTraining] = useMutation(CREATE_TRAINING, {
    onCompleted: (data) => {
      const createdTrainingId = data.createTraining.id;
      toast.success('Training created successfully');
      setIsCreateModalOpen(false);
      setTrainingData({ name: '', categoryId: '', levelId: '' });
      refetch();
      return createdTrainingId;
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create training')
  });

  const [updateTraining] = useMutation(UPDATE_TRAINING, {
    onCompleted: () => {
      toast.success('Training updated successfully');
      setIsEditModalOpen(false);
      setSelectedTraining(null);
      refetch();
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update training')
  });

  // Fetch trainings with pagination and filters
  const { loading, error, data: trainingsData, refetch } = useQuery(LIST_TRAININGS, {
    variables: {
      page,
      limit,
      filters: Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== null)
      )
    },
    onError: (error) => handleGraphQLError(error, 'Failed to load trainings')
  });

  // Fetch categories for filter dropdown
  const { data: categoriesData } = useQuery(GET_ALL_CATEGORIES, {
    onError: (error) => handleGraphQLError(error, 'Failed to load categories')
  });

  // Fetch levels for level filter dropdown
  const { data: levelsData } = useQuery(GET_ALL_LEVELS, {
    onError: (error) => handleGraphQLError(error, 'Failed to load levels')
  });

  // Filter handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value || null
    }));
    setPage(1);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      name: null,
      categoryId: null,
      levelId: null
    });
    setPage(1);
  };

  // Apply filters
  const handleApplyFilters = () => {
    refetch({
      page,
      limit,
      filters: Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== null)
      )
    });
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (trainingsData?.listTrainings?.totalPages > page) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <ArrowPathIcon className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
      </div>
    );
  }

  if (error || !trainingsData?.listTrainings) {
    return (
      <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        Failed to load trainings
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create Training Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsCreateModalOpen(false)}
            >
              <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
            </div>

            <div className={`
              inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all
              sm:my-8 sm:align-middle sm:max-w-lg sm:w-full
              ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
            `}>
              <div className={`
                px-4 pt-5 pb-4 sm:p-6 sm:pb-4
              `}>
                <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  Create New Training
                </h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (!trainingData.name) {
                    toast.error('Name is required');
                    return;
                  }
                  createTraining({
                    variables: {
                      input: {
                        name: trainingData.name,
                        categoryId: trainingData.categoryId || null,
                        levelId: trainingData.levelId || null
                      }
                    }
                  }).then((createdTrainingId) => {
                    // After successful training creation, upload thumbnail if file exists
                    if (thumbnailFile && createdTrainingId) {
                      const formData = new FormData();
                      formData.append('trainingId', createdTrainingId.data.createTraining.id);
                      formData.append('thumbnail', thumbnailFile);
                      const baseUrl = process.env.REACT_APP_API_BASE_URL;
                      const uploadUrl = `${baseUrl}/training/upload`;
                      fetch(uploadUrl, {
                        method: 'POST',
                        body: formData
                      })
                        .then(response => {
                          if (!response.ok) {
                            throw new Error('Failed to upload thumbnail');
                          }
                          return response.json();
                        })
                        .then(data => {
                          toast.success('Training created and thumbnail uploaded successfully');
                          setThumbnailFile(null);
                          refetch();
                        })
                        .catch(error => {
                          toast.error('Failed to upload thumbnail');
                        });
                    } else {
                      toast.success('Training created successfully');
                      refetch();
                    }
                    // After successful training creation, upload thumbnail if file exists
                    if (thumbnailFile) {
                      const formData = new FormData();
                      formData.append('trainingId', trainingsData?.listTrainings?.trainings[0]?.id);
                      formData.append('thumbnail', thumbnailFile);
                      const baseUrl = process.env.REACT_APP_API_BASE_URL;
                      const uploadUrl = `${baseUrl}/training/upload`;
                      fetch(uploadUrl, {
                        method: 'POST',
                        body: formData
                      })
                        .then(response => {
                          if (!response.ok) {
                            throw new Error('Failed to upload thumbnail');
                          }
                          return response.json();
                        })
                        .then(data => {
                          toast.success('Training created and thumbnail uploaded successfully');
                          setThumbnailFile(null);
                          refetch();
                        })
                        .catch(error => {
                          toast.error('Failed to upload thumbnail');
                        });
                    } else {
                      toast.success('Training created successfully');
                      refetch();
                    }
                  });
                }}>
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Name *
                      </label>
                      <input
                        type="text"
                        value={trainingData.name}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, name: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                        required
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Category
                      </label>
                      <select
                        value={trainingData.categoryId}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, categoryId: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                      >
                        <option value="">Select Category</option>
                        {categoriesData?.getAllCategories?.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Level
                      </label>
                      <select
                        value={trainingData.levelId}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, levelId: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                      >
                        <option value="">Select Level</option>
                        {levelsData?.getAllLevels?.map(level => (
                          <option key={level.id} value={level.id}>
                            {level.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Thumbnail
                      </label>
                      <div className={`
                        flex items-center space-x-2
                        ${isDarkMode
                          ? 'bg-gray-700 border-gray-600'
                          : 'bg-white border-gray-300'
                        }
                        border rounded-md p-2
                      `}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setThumbnailFile(e.target.files[0]);
                            }
                          }}
                          className={`
                            ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                          `}
                        />
                        {thumbnailFile && (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {thumbnailFile.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsCreateModalOpen(false)}
                      className={`
                        px-4 py-2 rounded-md text-sm font-medium
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }
                        transition-colors duration-300
                      `}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`
                        px-4 py-2 rounded-md text-sm font-medium text-white
                        ${isDarkMode
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-blue-500 hover:bg-blue-600'
                        }
                        transition-colors duration-300
                      `}
                    >
                      Create
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create New Training Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className={`
            flex items-center px-4 py-2 rounded-lg text-white font-semibold
            transition-all duration-300
            ${isDarkMode
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-500 hover:bg-blue-600'
            }
          `}
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create New Training
        </button>
      </div>

      {/* Filters */}
      <div className="grid md:grid-cols-4 gap-4 mb-4 items-end">
        <input
          type="text"
          name="name"
          value={filters.name || ''}
          onChange={handleFilterChange}
          placeholder="Filter by Name"
          className={`
            w-full px-4 py-2.5 
            ${isDarkMode
              ? 'bg-gray-700 text-gray-100 border-gray-600'
              : 'bg-white text-gray-900 border-gray-300'
            }
            border rounded-md 
            focus:outline-none focus:ring-2 
            ${isDarkMode
              ? 'focus:ring-blue-500 focus:border-blue-500'
              : 'focus:ring-blue-400 focus:border-blue-400'
            }
          `}
        />

        <select
          name="categoryId"
          value={filters.categoryId || ''}
          onChange={handleFilterChange}
          className={`
            w-full px-4 py-2.5 
            ${isDarkMode
              ? 'bg-gray-700 text-gray-100 border-gray-600'
              : 'bg-white text-gray-900 border-gray-300'
            }
            border rounded-md 
            focus:outline-none focus:ring-2 
            ${isDarkMode
              ? 'focus:ring-blue-500 focus:border-blue-500'
              : 'focus:ring-blue-400 focus:border-blue-400'
            }
          `}
        >
          <option value="">All Categories</option>
          {categoriesData?.getAllCategories?.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>

        <select
          name="levelId"
          value={filters.levelId || ''}
          onChange={handleFilterChange}
          className={`
            w-full px-4 py-2.5 
            ${isDarkMode
              ? 'bg-gray-700 text-gray-100 border-gray-600'
              : 'bg-white text-gray-900 border-gray-300'
            }
            border rounded-md 
            focus:outline-none focus:ring-2 
            ${isDarkMode
              ? 'focus:ring-blue-500 focus:border-blue-500'
              : 'focus:ring-blue-400 focus:border-blue-400'
            }
          `}
        >
          <option value="">All Levels</option>
          {levelsData?.getAllLevels?.map(level => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
        </select>

        <div className="flex gap-2">
          <button
            onClick={handleApplyFilters}
            className={`
              flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg
              text-white font-semibold transition-all duration-300
              ${isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-blue-500 hover:bg-blue-600'
              }
            `}
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            Filter
          </button>
          <button
            onClick={handleResetFilters}
            className={`
              flex items-center justify-center px-4 py-2.5 rounded-lg
              transition-all duration-300
              ${isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
              }
            `}
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Trainings Table */}
      <div className={`
        overflow-x-auto rounded-lg border
        ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
      `}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className={isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}>
            <tr>
              <th className={`
                px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Thumbnail
              </th>
              <th className={`
                px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Name
              </th>
              <th className={`
                px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Category
              </th>
              <th className={`
                px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Level
              </th>
              <th className={`
                px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Created At
              </th>
              <th className={`
                px-6 py-3 text-left text-xs font-medium uppercase tracking-wider
                ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`
            divide-y
            ${isDarkMode
              ? 'divide-gray-700 bg-gray-900'
              : 'divide-gray-200 bg-white'
            }
          `}>
            {trainingsData?.listTrainings?.trainings.map(training => (
              <tr key={training.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <img className="h-10 w-10 rounded" src={training.thumbnailURL || '/placeholder-image.png'} alt="Training thumbnail" />
                    </div>
                  </div>
                </td>
                <td className={`
                  px-6 py-4 whitespace-nowrap text-sm
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                `}>
                  {training.name}
                </td>
                <td className={`
                  px-6 py-4 whitespace-nowrap text-sm
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                `}>
                  {categoriesData?.getAllCategories?.find(c => c.id === training.categoryId)?.name || '-'}
                </td>
                <td className={`
                  px-6 py-4 whitespace-nowrap text-sm
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                `}>
                  {levelsData?.getAllLevels?.find(l => l.id === training.levelId)?.name || '-'}
                </td>
                <td className={`
                  px-6 py-4 whitespace-nowrap text-sm
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                `}>
                  {formatDate(training.createdAt)}
                </td>
                <td className={`
                  px-6 py-4 whitespace-nowrap text-sm flex items-center justify-center space-x-2
                  ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                `}>
                  <button
                    onClick={() => {
                      setSelectedTrainingId(training.id);
                      setIsAddExercisesModalOpen(true);
                    }}
                    className={`
                      px-3 py-1 rounded-md text-xs font-medium transition-colors duration-200 flex items-center
                      ${isDarkMode
                        ? 'bg-blue-700 text-white hover:bg-blue-600'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }
                    `}
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Exercises
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTraining(training);
                      setTrainingData({
                        name: training.name,
                        categoryId: training.categoryId,
                        levelId: training.levelId
                      });
                      setIsEditModalOpen(true);
                    }}
                    className={`
                      px-3 py-1 rounded-md text-xs font-medium transition-colors duration-200 flex items-center
                      ${isDarkMode
                        ? 'bg-yellow-700 text-white hover:bg-yellow-600'
                        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      }
                    `}
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Showing page {trainingsData?.listTrainings?.page} of {trainingsData?.listTrainings?.totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium
              ${isDarkMode
                ? page === 1
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : page === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }
            `}
          >
            Previous
          </button>
          <button
            onClick={handleNextPage}
            disabled={trainingsData?.listTrainings?.totalPages <= page}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium
              ${isDarkMode
                ? trainingsData?.listTrainings?.totalPages <= page
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : trainingsData?.listTrainings?.totalPages <= page
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }
            `}
          >
            Next
          </button>
        </div>
      </div>

      {/* Add Exercises Modal */}
      {isAddExercisesModalOpen && selectedTrainingId && (
        <AddExercisesToTrainingModal
          trainingId={selectedTrainingId}
          onClose={() => {
            setIsAddExercisesModalOpen(false);
            setSelectedTrainingId(null);
          }}
        />
      )}

      {/* Edit Training Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsEditModalOpen(false)}
            >
              <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
            </div>

            <div className={`
              inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all
              sm:my-8 sm:align-middle sm:max-w-lg sm:w-full
              ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
            `}>
              <div className={`
                px-4 pt-5 pb-4 sm:p-6 sm:pb-4
              `}>
                <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  Edit Training
                </h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const input = {
                    name: trainingData.name,
                    categoryId: trainingData.categoryId || null,
                    levelId: trainingData.levelId || null
                  };
                  updateTraining({
                    variables: {
                      input,
                      updateTrainingId: selectedTraining.id
                    }
                  }).then((response) => {
                    console.log(response);
                    // After successful training update, upload thumbnail if file exists
                    if (editThumbnailFile) {
                      const formData = new FormData();
                      formData.append('trainingId', selectedTraining.id);
                      formData.append('thumbnail', editThumbnailFile);

                      const baseUrl = process.env.REACT_APP_API_BASE_URL;
                      const uploadUrl = `${baseUrl}/training/upload`;
                      return fetch(uploadUrl, {
                        method: 'POST',
                        body: formData
                      })
                        .then(response => {
                          if (!response.ok) {
                            throw new Error('Failed to upload thumbnail');
                          }
                          return response.json();
                        })
                        .then(data => {
                          toast.success('Training updated and thumbnail uploaded successfully');
                          setEditThumbnailFile(null);
                          refetch();
                        })
                        .catch(error => {
                          toast.error('Failed to upload thumbnail');
                        });
                    } else {
                      toast.success('Training updated successfully');
                      refetch();
                    }
                  }).catch(error => {
                    toast.error('Failed to update training');
                  });
                }}>
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Name *
                      </label>
                      <input
                        type="text"
                        value={trainingData.name}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, name: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                        required
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Category
                      </label>
                      <select
                        value={trainingData.categoryId}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, categoryId: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                      >
                        <option value="">Select Category</option>
                        {categoriesData?.getAllCategories?.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Level
                      </label>
                      <select
                        value={trainingData.levelId}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, levelId: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                      >
                        <option value="">Select Level</option>
                        {levelsData?.getAllLevels?.map(level => (
                          <option key={level.id} value={level.id}>
                            {level.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Thumbnail
                      </label>
                      <div className={`
                        flex items-center space-x-2
                        ${isDarkMode
                          ? 'bg-gray-700 border-gray-600'
                          : 'bg-white border-gray-300'
                        }
                        border rounded-md p-2
                      `}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setEditThumbnailFile(e.target.files[0]);
                            }
                          }}
                          className={`
                            ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                          `}
                        />
                        {thumbnailFile && (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {thumbnailFile.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditModalOpen(false);
                        setSelectedTraining(null);
                        setTrainingData({ name: '', categoryId: '', levelId: '' });
                        setThumbnailFile(null);
                      }}
                      className={`
                        px-4 py-2 rounded-md text-sm font-medium
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }
                        transition-colors duration-300
                      `}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`
                        px-4 py-2 rounded-md text-sm font-medium text-white
                        ${isDarkMode
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-blue-500 hover:bg-blue-600'
                        }
                        transition-colors duration-300
                      `}
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Edit Training Modal
  const renderEditModal = () => {
    if (!selectedTraining) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 transition-opacity"
            onClick={() => setIsEditModalOpen(false)}
          >
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
          </div>

          <div className={`
            inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all
            sm:my-8 sm:align-middle sm:max-w-lg sm:w-full
            ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
          `}>
            <div className={`
              px-4 pt-5 pb-4 sm:p-6 sm:pb-4
            `}>
              <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                Edit Training
              </h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                const input = {
                  name: trainingData.name,
                  categoryId: trainingData.categoryId || null,
                  levelId: trainingData.levelId || null
                };
                updateTraining({
                  variables: {
                    input,
                    updateTrainingId: selectedTraining.id
                  }
                });
              }}>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={trainingData.name}
                      onChange={(e) => setTrainingData(prev => ({ ...prev, name: e.target.value }))}
                      className={`
                        w-full px-3 py-2 rounded-md
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-100 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                        }
                        border focus:outline-none focus:ring-2
                        ${isDarkMode
                          ? 'focus:ring-blue-500 focus:border-blue-500'
                          : 'focus:ring-blue-400 focus:border-blue-400'
                        }
                      `}
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Category
                    </label>
                    <select
                      value={trainingData.categoryId}
                      onChange={(e) => setTrainingData(prev => ({ ...prev, categoryId: e.target.value }))}
                      className={`
                        w-full px-3 py-2 rounded-md
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-100 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                        }
                        border focus:outline-none focus:ring-2
                        ${isDarkMode
                          ? 'focus:ring-blue-500 focus:border-blue-500'
                          : 'focus:ring-blue-400 focus:border-blue-400'
                        }
                      `}
                    >
                      <option value="">Select Category</option>
                      {categoriesData?.getAllCategories?.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Level
                    </label>
                    <select
                      value={trainingData.levelId}
                      onChange={(e) => setTrainingData(prev => ({ ...prev, levelId: e.target.value }))}
                      className={`
                        w-full px-3 py-2 rounded-md
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-100 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                        }
                        border focus:outline-none focus:ring-2
                        ${isDarkMode
                          ? 'focus:ring-blue-500 focus:border-blue-500'
                          : 'focus:ring-blue-400 focus:border-blue-400'
                        }
                      `}
                    >
                      <option value="">Select Level</option>
                      {levelsData?.getAllLevels?.map(level => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Thumbnail
                    </label>
                    <div className={`
                      flex items-center space-x-2
                      ${isDarkMode
                        ? 'bg-gray-700 border-gray-600'
                        : 'bg-white border-gray-300'
                      }
                      border rounded-md p-2
                    `}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setThumbnailFile(e.target.files[0]);
                          }
                        }}
                        className={`
                          ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                        `}
                      />
                      {thumbnailFile && (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {thumbnailFile.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedTraining(null);
                      setTrainingData({ name: '', categoryId: '', levelId: '' });
                      setThumbnailFile(null);
                    }}
                    className={`
                      px-4 py-2 rounded-md text-sm font-medium
                      ${isDarkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }
                      transition-colors duration-300
                    `}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`
                      px-4 py-2 rounded-md text-sm font-medium text-white
                      ${isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-blue-500 hover:bg-blue-600'
                      }
                      transition-colors duration-300
                    `}
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Create Training Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsCreateModalOpen(false)}
            >
              <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
            </div>

            <div className={`
              inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all
              sm:my-8 sm:align-middle sm:max-w-lg sm:w-full
              ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
            `}>
              <div className={`
                px-4 pt-5 pb-4 sm:p-6 sm:pb-4
              `}>
                <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  Create New Training
                </h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (!trainingData.name) {
                    toast.error('Name is required');
                    return;
                  }
                  createTraining({
                    variables: {
                      input: {
                        name: trainingData.name,
                        categoryId: trainingData.categoryId || null,
                        levelId: trainingData.levelId || null
                      }
                    }
                  }).then((createdTrainingId) => {
                    // After successful training creation, upload thumbnail if file exists
                    if (thumbnailFile && createdTrainingId) {
                      const formData = new FormData();
                      formData.append('trainingId', createdTrainingId);
                      formData.append('thumbnail', thumbnailFile);
                      const baseUrl = process.env.REACT_APP_API_BASE_URL;
                      const uploadUrl = `${baseUrl}/training/upload`;

                      fetch(uploadUrl, {
                        method: 'POST',
                        body: formData
                      })
                        .then(response => {
                          if (!response.ok) {
                            throw new Error('Failed to upload thumbnail');
                          }
                          return response.json();
                        })
                        .then(data => {
                          toast.success('Training created and thumbnail uploaded successfully');
                          setThumbnailFile(null);
                          refetch();
                        })
                        .catch(error => {
                          toast.error('Failed to upload thumbnail');
                        });
                    } else {
                      toast.success('Training created successfully');
                      refetch();
                    }
                  });
                }}>
                  <div className="space-y-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Name *
                      </label>
                      <input
                        type="text"
                        value={trainingData.name}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, name: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                        required
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Category
                      </label>
                      <select
                        value={trainingData.categoryId}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, categoryId: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                      >
                        <option value="">Select Category</option>
                        {categoriesData?.getAllCategories?.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Level
                      </label>
                      <select
                        value={trainingData.levelId}
                        onChange={(e) => setTrainingData(prev => ({ ...prev, levelId: e.target.value }))}
                        className={`
                          w-full px-3 py-2 rounded-md
                          ${isDarkMode
                            ? 'bg-gray-700 text-gray-100 border-gray-600'
                            : 'bg-white text-gray-900 border-gray-300'
                          }
                          border focus:outline-none focus:ring-2
                          ${isDarkMode
                            ? 'focus:ring-blue-500 focus:border-blue-500'
                            : 'focus:ring-blue-400 focus:border-blue-400'
                          }
                        `}
                      >
                        <option value="">Select Level</option>
                        {levelsData?.getAllLevels?.map(level => (
                          <option key={level.id} value={level.id}>
                            {level.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Thumbnail
                      </label>
                      <div className={`
                        flex items-center space-x-2
                        ${isDarkMode
                          ? 'bg-gray-700 border-gray-600'
                          : 'bg-white border-gray-300'
                        }
                        border rounded-md p-2
                      `}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setThumbnailFile(e.target.files[0]);
                            }
                          }}
                          className={`
                            ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}
                          `}
                        />
                        {thumbnailFile && (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {thumbnailFile.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setIsCreateModalOpen(false);
                        setTrainingData({ name: '', categoryId: '', levelId: '' });
                        setThumbnailFile(null);
                      }}
                      className={`
                        px-4 py-2 rounded-md text-sm font-medium
                        ${isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }
                        transition-colors duration-300
                      `}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`
                        px-4 py-2 rounded-md text-sm font-medium text-white
                        ${isDarkMode
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-blue-500 hover:bg-blue-600'
                        }
                        transition-colors duration-300
                      `}
                    >
                      Create
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Training Modal */}
      {isEditModalOpen && renderEditModal()}

    </div>
  );
};

export default TrainingsManagement;
